import { Link } from "react-router-dom";
import { Recycle, Droplets, Factory, ArrowRight, Trash2, FlaskConical } from "lucide-react";

const categories = [
  {
    title: "Plastic Recycling Infrastructure",
    icon: Recycle,
    items: ["PET bottle recycling plants", "Multi-layer plastic processing", "Plastic-to-fuel conversion units", "Plastic road material production"],
    who: "Waste management companies, municipalities, CSR programs",
  },
  {
    title: "Solid Waste Processing",
    icon: Trash2,
    items: ["Material Recovery Facilities (MRF)", "Composting plants", "RDF (Refuse Derived Fuel) production", "Integrated solid waste management"],
    who: "Urban local bodies, industrial zones, smart cities",
  },
  {
    title: "Biomedical Waste Treatment",
    icon: FlaskConical,
    items: ["Autoclaving & shredding facilities", "Incineration plants", "Deep burial sites", "Sharp waste management"],
    who: "Hospitals, diagnostic centers, pharma companies",
  },
  {
    title: "Water Treatment & ZLD",
    icon: Droplets,
    items: ["ETP (Effluent Treatment Plants)", "STP (Sewage Treatment Plants)", "WTP (Water Treatment Plants)", "Zero Liquid Discharge Systems"],
    who: "Manufacturing units, textiles, pharma, municipalities",
  },
  {
    title: "Industrial Recycling",
    icon: Factory,
    items: ["Metal scrap recycling", "Electronic waste processing", "Fly ash utilization", "Slag and by-product recovery"],
    who: "Manufacturing units, heavy industries, foundries",
  },
];

export default function WasteInfrastructurePage() {
  return (
    <div className="pt-20" data-testid="waste-infrastructure-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-orange-950 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium mb-6">
              <Recycle className="h-4 w-4" /> Circular Economy
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Waste & Recycling Infrastructure
            </h1>
            <p className="mt-6 text-xl text-slate-400 leading-relaxed">
              Design, develop, and deploy waste management and recycling infrastructure — from municipal solutions to industrial systems.
            </p>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-all">
              Discuss Your Requirements <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {categories.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <div key={i} className="grid lg:grid-cols-3 gap-8 items-start">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                      <Icon className="h-6 w-6 text-orange-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">{cat.title}</h2>
                  </div>
                  <p className="text-slate-600 mb-4">For: <span className="font-medium">{cat.who}</span></p>
                  <Link to="/contact" className="text-sm font-semibold text-orange-600 hover:text-orange-700 flex items-center gap-1">
                    Start Project <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
                <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
                  {cat.items.map((item, j) => (
                    <div key={j} className="p-4 rounded-xl bg-orange-50 border border-orange-100 hover:border-orange-200 transition-all">
                      <span className="text-slate-800 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-orange-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Need Waste Infrastructure?</h2>
          <p className="text-orange-100 text-lg mb-8">We handle concept, design, execution, and operations for all waste management projects.</p>
          <Link to="/contact" className="px-8 py-3 bg-white text-orange-700 font-bold rounded-lg hover:bg-orange-50 transition-all inline-flex items-center gap-2">
            Discuss Requirements <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
